// The tick of a check row (#92's Roll Call control, #94's agreement): a 22 px button role="checkbox" in the check
// glyph's geometry. Space or a click flips it. DC-24: blocked keeps the tab stop, is aria-disabled, points
// aria-describedby at the inline reason (mirrored by the tooltip) and does nothing; the caller draws no CheckTick at all
// for `mode="view"` or `can={false}`.

import { forwardRef, type MouseEvent } from "react";
import { Tooltip } from "../lib/Tooltip";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import type { Blocked } from "../core/Button";
import { Icon } from "../core/icons";

export interface CheckTickProps {
  checked: boolean;
  onChange?: ((checked: boolean) => void) | undefined;
  /** The id of the words that name it (the person's name, the agreement's words). */
  labelledBy: string;
  blocked?: Blocked | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export const CheckTick = forwardRef<HTMLButtonElement, CheckTickProps>(function CheckTick(
  { checked, onChange, labelledBy, blocked, hs, className },
  ref,
) {
  const hot = useHs(hs);
  const why = `${useSvgId()}-why`;
  const flip = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      event.preventDefault();
      return;
    }
    onChange?.(!checked);
  };
  const button = (
    <button
      ref={ref}
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-disabled={blocked ? true : undefined}
      aria-describedby={blocked ? why : undefined}
      className={cx("mc-tick", checked && "mc-on", blocked && "mc-dis", className)}
      onClick={flip}
      {...hot}
    >
      {checked ? <Icon name="check" size={14} /> : null}
    </button>
  );
  if (!blocked) return button;
  return (
    <>
      <Tooltip label={blocked.reason} mode="mirror">{button}</Tooltip>
      <VisuallyHidden id={why}>{blocked.reason}</VisuallyHidden>
    </>
  );
});
